import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";

import axios from "@axios";
import { useRouter } from "@core/utils";
import store from "@/store";
import { computed } from "@vue/composition-api";

/**
 * Helps manage several axios calls on a same page.
 *
 * It centralize axios actions, logs URLs being loaded
 * and provides a isLoading state until all URLs have not finished to load.
 */
const useAxios = () => {
  const { route } = useRouter();
  const page = route.value.path.replace(/^(\/)?/, "").replaceAll("/", "_");
  const supportedMethods = ["get", "post", "put", "patch", "delete"];

  const axiosGet = async (url, params, options = {}) => {
    addLoading("get", url);
    try {
      return await axios.get(url, { params, ...options });
    } finally {
      removeLoading("get", url);
    }
  };

  const axiosPost = async (url, params, options = undefined) => {
    addLoading("post", url);
    try {
      return await axios.post(url, params, options);
    } finally {
      removeLoading("post", url);
    }
  };

  const axiosPut = async (url, params) => {
    addLoading("put", url);
    try {
      return await axios.put(url, params);
    } finally {
      removeLoading("put", url);
    }
  };

  const axiosPatch = async (url, params) => {
    addLoading("patch", url);
    try {
      return await axios.patch(url, params);
    } finally {
      removeLoading("patch", url);
    }
  };

  const axiosDelete = async (url, params) => {
    addLoading("delete", url);
    try {
      return await axios.delete(url, { params });
    } finally {
      removeLoading("delete", url);
    }
  };

  const loadingUrls = computed(() => {
    let urls = [];
    for (const method of supportedMethods) {
      urls = urls.concat(
        get(store.getters["axios/getLoadingUrls"], `${page}.${method}`, [])
      );
    }

    // Unique:
    return [...new Set([...urls])];
  });

  const isLoading = computed(() => {
    return supportedMethods
      .map(
        (method) =>
          !isEmpty(
            get(
              store.getters["axios/getLoadingUrls"],
              `${page}.${method}`,
              undefined
            )
          )
      )
      .some((value) => value);
  });

  const addLoading = (method, url) => {
    store.dispatch("axios/updateLoadingUrls", {
      name: `${page}.${method}`,
      value: (state) => uniq([...(state ?? []), url]),
    });
  };

  const removeLoading = (method, url) => {
    store.dispatch("axios/updateLoadingUrls", {
      name: `${page}.${method}`,
      value: (state) => [...(state ?? []).filter((link) => link !== url)],
    });
  };

  return {
    isLoading,
    loadingUrls,
    axiosGet,
    axiosPost,
    axiosPut,
    axiosPatch,
    axiosDelete,
  };
};

export default useAxios;
